import { useState } from 'react';

import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import { Box, Button, Card, Grid, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useQuestionnaires } from '../../../hooks/useQuestionnaires';
import { gridSpacing } from '../../../store/constant';
import BulkAssignDialog from './BulkAssignDialog';
import ExistingQuestionnaireCard from './QuestionarioCard';

const actionButtonStyles = {
    height: 46,
    borderRadius: 1.5,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    textTransform: 'none'
};

const QuestionnaireManager = () => {
    const { t } = useTranslation();
    const {
        data: existingQuestionnaires,
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count
    } = useQuestionnaires();

    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);

    const toggleSelection = (uuid) => {
        setSelectedQuestionnaires((previous) =>
            previous.includes(uuid)
                ? previous.filter((questionnaireUuid) => questionnaireUuid !== uuid)
                : [...previous, uuid]
        );
    };

    const cancelSelection = () => {
        setSelectionMode(false);
        setSelectedQuestionnaires([]);
    };

    return (
        <Box sx={{ minHeight: 'calc(100vh - 80px)', p: 2, bgcolor: '#F4F6F8' }}>
            <Box
                component="header"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mb: 2
                }}
            >
                {!selectionMode ? (
                    <Button
                        variant="contained"
                        startIcon={<ChecklistOutlinedIcon />}
                        onClick={() => setSelectionMode(true)}
                        sx={{ ...actionButtonStyles, minWidth: 205 }}
                    >
                        {t('questionnaires.select')}
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            startIcon={<AssignmentTurnedInOutlinedIcon />}
                            onClick={() => setOpenAssignDialog(true)}
                            disabled={selectedQuestionnaires.length === 0}
                            sx={{ ...actionButtonStyles, minWidth: 205 }}
                        >
                            {t('questionnaires.assign_selected')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={cancelSelection}
                            sx={{ ...actionButtonStyles, minWidth: 112 }}
                        >
                            {t('questionnaires.cancel')}
                        </Button>
                    </>
                )}
            </Box>

            <Card
                variant="outlined"
                sx={{
                    overflow: 'hidden',
                    borderColor: '#E0E5E9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
                }}
            >
                <Grid
                    container
                    spacing={2}
                    justifyContent="space-around"
                    alignItems="start"
                    my={2}
                >
                    <Grid item xs={11}>
                        <Grid container spacing={gridSpacing} justifyContent="start">
                            {Array.isArray(existingQuestionnaires) &&
                                existingQuestionnaires.map((questionnaire) => (
                                    <Grid
                                        item
                                        key={questionnaire.uuid}
                                        lg={4}
                                        md={6}
                                        sm={12}
                                        xs={12}
                                    >
                                        <ExistingQuestionnaireCard
                                            questionnaire={questionnaire}
                                            selectionMode={selectionMode}
                                            selected={selectedQuestionnaires.includes(
                                                questionnaire.uuid
                                            )}
                                            onToggleSelect={() =>
                                                toggleSelection(questionnaire.uuid)
                                            }
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                    </Grid>
                </Grid>

                <TablePagination
                    component="div"
                    count={count}
                    page={paginationModel.page}
                    onPageChange={(_, newPage) =>
                        setPaginationModel({ ...paginationModel, page: newPage })
                    }
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={(event) =>
                        setPaginationModel({
                            ...paginationModel,
                            page: 0,
                            pageSize: parseInt(event.target.value, 10)
                        })
                    }
                    rowsPerPageOptions={rowsPerPageOptions}
                />
            </Card>

            <BulkAssignDialog
                open={openAssignDialog}
                onClose={() => setOpenAssignDialog(false)}
                questionnaireUUIDs={selectedQuestionnaires}
            />
        </Box>
    );
};

export default QuestionnaireManager;
